// src/contexts/AIInsightsContext.js
// FlowSense — Enterprise AI Insights Context (Standalone / Redux-Free)
// Provides real-time AI-driven operational intelligence across the entire app tree.
//
// Architecture:
//   - Single provider mount     : one hook instance, zero duplicate API calls across the tree
//   - WebSocket fan-out         : watchdog_events + work_order_events + asset_events streams
//   - Standalone State          : 100% Redux-free. Manages its own global metrics and hydration
//   - localStorage cache        : TTL-aware with deep-equality dirty-check (no stale re-renders)
//   - Config injection          : admin thresholds pulled from /api/admin/configurations
//   - Role-aware scoping        : insights filtered to the authenticated user's permission level
//   - DebugLogger               : colour-coded console output (dev only, zero prod overhead)
//   - AbortController cleanup   : no setState calls after unmount
//   - Derived metrics memo      : heavy computations behind useMemo guards
//   - Granular consumer hooks   : components subscribe to only the slice they need
//   - Stale-while-revalidate    : cache served immediately; background refresh in parallel

import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    useReducer,
    useState
} from 'react';
import { useToast }                 from '@chakra-ui/react';

// --- FlowSense service layer ---
import api                          from '../services/api';           // axios instance with auth headers
import webSocketService             from '../services/websocketService';
import { useAIInsights }            from '../hooks/useAIInsights';
import { getUserInfo }              from '../utils/auth';             // Used natively instead of Redux

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const CTX_NAME      = 'AIInsightsContext';
const CACHE_NS      = 'fs_ai_insights_';        // localStorage namespace prefix

const CACHE_KEYS = {
    CONFIG:        `${CACHE_NS}config`,
    INSIGHTS:      `${CACHE_NS}insights`,
    WATCHDOG:      `${CACHE_NS}watchdog`,
    ASSET_HEALTH:  `${CACHE_NS}asset_health`,
};

const CACHE_TTL = {
    CONFIG:        10 * 60 * 1000,   // 10 min  — admin config rarely changes
    INSIGHTS:       5 * 60 * 1000,   //  5 min  — AI analysis cadence
    WATCHDOG:      30 * 1000,        // 30 sec  — watchdog ticks frequently
    ASSET_HEALTH:   2 * 60 * 1000,   //  2 min  — RUL/health scores
};

const WS_CHANNELS = {
    WATCHDOG:     'watchdog_events',
    WORK_ORDERS:  'work_order_events',
    ASSETS:       'asset_events',
    NOTIFICATIONS:'notification_events',
};

const ROLE_LEVELS = {
    technician: 1,
    supervisor: 2,
    manager:    3,
    admin:      4,
};

const DEFAULT_CONFIG = {
    aiRefreshIntervalMs:       5 * 60 * 1000,  // 5 min
    watchdogPollIntervalMs:   30 * 1000,        // 30 sec
    slaAtRiskHours:            4,
    stagnantHours:            24,
    aiNeglectHours:            6,
    unassignedCriticalHours:   2,
    enablePredictiveInsights:  true,
    enableDispatchSuggestions: true,
    enableSlaForecasting:      true,
    maxInsightAge:            30,               // days — for historical queries
};

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG LOGGER
// Zero-cost in production. Colour-coded in development (matches FlowSense pattern).
// ─────────────────────────────────────────────────────────────────────────────

class DebugLogger {
    constructor(namespace, color = '#9c6bff') {
        this._ns    = namespace;
        this._color = color;
        this._enabled = process.env.NODE_ENV === 'development';
    }

    _fmt(level, color) {
        return [
            `%c[${this._ns}]%c ${level}`,
            `color:${this._color};font-weight:bold`,
            `color:${color}`,
        ];
    }

    info  (...args) { if (this._enabled) console.info (...this._fmt('INFO',  '#4ade80'), ...args); }
    warn  (...args) { if (this._enabled) console.warn (...this._fmt('WARN',  '#facc15'), ...args); }
    error (...args) { if (this._enabled) console.error(...this._fmt('ERROR', '#f87171'), ...args); }
    debug (...args) { if (this._enabled) console.debug(...this._fmt('DEBUG', '#60a5fa'), ...args); }
    group (label)   { if (this._enabled) console.groupCollapsed(`%c[${this._ns}] ${label}`, `color:${this._color};font-weight:bold`); }
    groupEnd()      { if (this._enabled) console.groupEnd(); }
}

const log = new DebugLogger(CTX_NAME, '#a78bfa');

// ─────────────────────────────────────────────────────────────────────────────
// CACHE UTILITIES (localStorage, TTL-aware, deep-equality dirty check)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reads a cached value if it exists and has not expired.
 * Returns null on miss, expiry, or parse failure.
 */
const cacheRead = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const { value, expiresAt } = JSON.parse(raw);
        if (Date.now() > expiresAt) {
            localStorage.removeItem(key);
            return null;
        }
        return value;
    } catch {
        return null;
    }
};

/**
 * Writes a value to localStorage under the given key with a TTL.
 * Only writes if the new value differs from the cached value (deep equality).
 */
const cacheWrite = (key, value, ttlMs) => {
    try {
        const existing = cacheRead(key);
        if (JSON.stringify(existing) === JSON.stringify(value)) return; // deep-equal, skip write
        localStorage.setItem(key, JSON.stringify({ value, expiresAt: Date.now() + ttlMs }));
    } catch {
        /* storage quota — silently ignore */
    }
};

/**
 * Invalidates a specific cache key or all keys in the namespace.
 */
const cacheInvalidate = (key = null) => {
    if (key) {
        localStorage.removeItem(key);
        return;
    }
    // Full namespace flush
    Object.keys(localStorage)
        .filter(k => k.startsWith(CACHE_NS))
        .forEach(k => localStorage.removeItem(k));
};

// ─────────────────────────────────────────────────────────────────────────────
// INTERNAL STATE REDUCER
// Keeps all async state transitions explicit and testable.
// ─────────────────────────────────────────────────────────────────────────────

const ACTIONS = {
    SET_CONFIG:        'SET_CONFIG',
    SET_WATCHDOG:      'SET_WATCHDOG',
    SET_ASSET_HEALTH:  'SET_ASSET_HEALTH',
    SET_LIVE_EVENTS:   'SET_LIVE_EVENTS',
    SET_ERROR:         'SET_ERROR',
    SET_LOADING:       'SET_LOADING',
    RESET_ERROR:       'RESET_ERROR',
    RECORD_WS_EVENT:   'RECORD_WS_EVENT',
};

const initialState = {
    config:         cacheRead(CACHE_KEYS.CONFIG) ?? DEFAULT_CONFIG,
    watchdogHealth: cacheRead(CACHE_KEYS.WATCHDOG) ?? null,
    assetHealth:    cacheRead(CACHE_KEYS.ASSET_HEALTH) ?? null,
    liveEvents:     [],         // Ring buffer of last 50 WebSocket events
    error:          null,
    isLoadingConfig: false,
    isLoadingWatchdog: false,
    isLoadingAssets:   false,
};

const insightsReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_CONFIG:
            return { ...state, config: action.payload, isLoadingConfig: false };
        case ACTIONS.SET_WATCHDOG:
            return { ...state, watchdogHealth: action.payload, isLoadingWatchdog: false };
        case ACTIONS.SET_ASSET_HEALTH:
            return { ...state, assetHealth: action.payload, isLoadingAssets: false };
        case ACTIONS.SET_LOADING:
            return { ...state, [`isLoading${action.payload}`]: true };
        case ACTIONS.SET_ERROR:
            return { ...state, error: action.payload };
        case ACTIONS.RESET_ERROR:
            return { ...state, error: null };
        case ACTIONS.RECORD_WS_EVENT: {
            // Ring buffer: keep last 50 live events
            const updated = [action.payload, ...state.liveEvents].slice(0, 50);
            return { ...state, liveEvents: updated };
        }
        default:
            return state;
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT DEFINITION
// ─────────────────────────────────────────────────────────────────────────────

const AIInsightsContext = createContext(null);
AIInsightsContext.displayName = CTX_NAME; // visible in React DevTools

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

export const AIInsightsProvider = ({ children }) => {

    // ── Auth State (Native via Utils, Redux-Free) ───────────────────────────
    const currentUser   = getUserInfo();
    const userRole      = currentUser?.role ?? 'technician';
    const userRoleLevel = ROLE_LEVELS[userRole] ?? 1;

    // ── Independent Data State (Replaces Redux) ─────────────────────────────
    const [globalMetrics, setGlobalMetrics]       = useState({});
    const [activeWorkOrders, setActiveWorkOrders] = useState([]); // Pages can push to this if needed

    // ── Internal async state ────────────────────────────────────────────────
    const [state, localDispatch] = useReducer(insightsReducer, initialState);

    // ── Refs ────────────────────────────────────────────────────────────────
    const abortRef          = useRef(null);    // AbortController for in-flight fetches
    const wsUnsubs          = useRef([]);       // WS unsubscribe callbacks
    const configIntervalRef = useRef(null);
    const watchdogIntervalRef = useRef(null);
    const isMountedRef      = useRef(true);

    // ── Chakra toast for elevated alerts ───────────────────────────────────
    const toast = useToast();

    // ─────────────────────────────────────────────────────────────────────────
    // CORE AI HOOK — initialised at root level, single instance for the tree
    // ─────────────────────────────────────────────────────────────────────────

    const insightsData = useAIInsights(activeWorkOrders, globalMetrics, {
        enabled:         true,
        autoRefresh:     true,
        refreshInterval: state.config.aiRefreshIntervalMs,
        debugMode:       process.env.NODE_ENV === 'development',
        currentUser,
        userRole,
    });

    // ─────────────────────────────────────────────────────────────────────────
    // SAFE STATE SETTER — guards against post-unmount setState
    // ─────────────────────────────────────────────────────────────────────────

    const safeDispatch = useCallback((action) => {
        if (isMountedRef.current) localDispatch(action);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // FETCHERS (Standalone API Calls)
    // ─────────────────────────────────────────────────────────────────────────

    const fetchAdminConfig = useCallback(async (signal) => {
        // Serve cache immediately (stale-while-revalidate)
        const cached = cacheRead(CACHE_KEYS.CONFIG);
        if (cached) {
            safeDispatch({ type: ACTIONS.SET_CONFIG, payload: cached });
            log.debug('Admin config — served from cache.');
        }

        try {
            safeDispatch({ type: ACTIONS.SET_LOADING, payload: 'Config' });

            const { data } = await api.get('/admin/configurations', {
                signal,
                params: {
                    keys: [
                        'ai_refresh_interval_ms',
                        'watchdog_poll_interval_ms',
                        'watchdog_sla_at_risk_hours',
                        'watchdog_stagnant_hours',
                        'watchdog_ai_neglect_hours',
                        'watchdog_unassigned_critical_hours',
                        'enable_predictive_insights',
                        'enable_dispatch_suggestions',
                        'enable_sla_forecasting',
                        'max_insight_age_days',
                    ].join(','),
                },
            });

            const normalized = normalizeAdminConfig(data?.configurations ?? []);
            cacheWrite(CACHE_KEYS.CONFIG, normalized, CACHE_TTL.CONFIG);
            safeDispatch({ type: ACTIONS.SET_CONFIG, payload: normalized });
            log.info('Admin config refreshed.', normalized);

        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            log.warn('Admin config fetch failed — using cached/default.', err.message);
        }
    }, [safeDispatch]);

    const fetchWatchdogHealth = useCallback(async (signal) => {
        if (userRoleLevel < ROLE_LEVELS.manager) return; // Permission gate

        const cached = cacheRead(CACHE_KEYS.WATCHDOG);
        if (cached) safeDispatch({ type: ACTIONS.SET_WATCHDOG, payload: cached });

        try {
            safeDispatch({ type: ACTIONS.SET_LOADING, payload: 'Watchdog' });

            const { data } = await api.get('/admin/watchdog/health', { signal });
            const health   = data?.health ?? null;

            if (health) {
                cacheWrite(CACHE_KEYS.WATCHDOG, health, CACHE_TTL.WATCHDOG);
                safeDispatch({ type: ACTIONS.SET_WATCHDOG, payload: health });
                log.debug('Watchdog health refreshed.', { efficiency: health.last_scan?.efficiency_rating });
            }

        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            log.warn('Watchdog health fetch failed.', err.message);
        }
    }, [userRoleLevel, safeDispatch]);

    // NEW: Natively pull the 11k+ metrics without Redux
    const fetchGlobalMetrics = useCallback(async (signal) => {
        try {
            const { data } = await api.get('/workorders/watchdog/metrics', { signal });
            if (data?.data) {
                setGlobalMetrics(data.data);
            }
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            log.warn('Failed to fetch global watchdog metrics');
        }
    }, []);

    const fetchAssetHealth = useCallback(async (signal) => {
        const cached = cacheRead(CACHE_KEYS.ASSET_HEALTH);
        if (cached) safeDispatch({ type: ACTIONS.SET_ASSET_HEALTH, payload: cached });

        try {
            safeDispatch({ type: ACTIONS.SET_LOADING, payload: 'Assets' });

            const { data } = await api.get('/assets/health-snapshot', { signal });
            const snapshot  = data?.snapshot ?? null;

            if (snapshot) {
                cacheWrite(CACHE_KEYS.ASSET_HEALTH, snapshot, CACHE_TTL.ASSET_HEALTH);
                safeDispatch({ type: ACTIONS.SET_ASSET_HEALTH, payload: snapshot });
                log.debug('Asset health snapshot refreshed.', { count: snapshot.asset_count });
            }

        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            log.warn('Asset health fetch failed.', err.message);
        }
    }, [safeDispatch]);

    // ─────────────────────────────────────────────────────────────────────────
    // WEBSOCKET SUBSCRIPTIONS
    // Listens to all relevant FlowSense channels and hydrates context state.
    // ─────────────────────────────────────────────────────────────────────────

    const attachWebSocketListeners = useCallback(() => {
        if (!webSocketService) {
            log.warn('webSocketService not available — real-time updates disabled.');
            return;
        }

        log.info('Attaching WebSocket listeners…');

        // ── watchdog_events ───────────────────────────────────────────────
        const onWatchdogEvent = (event) => {
            if (!isMountedRef.current) return;
            log.debug(`[WS:watchdog_events] type=${event.type}`);
            safeDispatch({ type: ACTIONS.RECORD_WS_EVENT, payload: { channel: WS_CHANNELS.WATCHDOG, ...event } });

            switch (event.type) {
                case 'scan_complete': {
                    // Live watchdog report from the backend scan
                    const health = {
                        scan_id:          event.scan_id,
                        efficiency_rating: event.efficiency_rating,
                        metrics:          event.metrics,
                        corrections:      event.corrections,
                        timestamp:        event.timestamp,
                        circuit_state:    'CLOSED',
                    };
                    safeDispatch({ type: ACTIONS.SET_WATCHDOG, payload: health });
                    cacheWrite(CACHE_KEYS.WATCHDOG, health, CACHE_TTL.WATCHDOG);
                    fetchGlobalMetrics(); // Silently pull new metrics after scan
                    break;
                }
                case 'circuit_open':
                    log.error('Watchdog circuit OPEN — scans paused.', event);
                    if (userRoleLevel >= ROLE_LEVELS.manager) {
                        toast({
                            title:       '⚠ Watchdog Circuit Breaker Tripped',
                            description: 'Work order automated scans have been paused. Check system health.',
                            status:      'error',
                            duration:    10_000,
                            isClosable:  true,
                            position:    'top-right',
                        });
                    }
                    break;
                case 'circuit_closed':
                    log.info('Watchdog circuit CLOSED — scans resumed.');
                    break;
                default:
                    break;
            }
        };

        // ── work_order_events ─────────────────────────────────────────────
        const onWorkOrderEvent = (event) => {
            if (!isMountedRef.current) return;
            log.debug(`[WS:work_order_events] type=${event.type}, woId=${event.workOrderId}`);
            safeDispatch({ type: ACTIONS.RECORD_WS_EVENT, payload: { channel: WS_CHANNELS.WORK_ORDERS, ...event } });
            
            // Keep stats fresh when work orders change
            fetchGlobalMetrics();

            switch (event.type) {
                case 'unassigned_critical':
                    if (userRoleLevel >= ROLE_LEVELS.manager) {
                        toast({
                            title:       `🚨 Unassigned ${(event.priority ?? '').toUpperCase()} Work Order`,
                            description: `Work order #${event.workOrderId} has been unassigned for ${event.hoursUnassigned}h.`,
                            status:      'warning',
                            duration:    8_000,
                            isClosable:  true,
                            position:    'top-right',
                        });
                    }
                    break;
                case 'stagnant_flagged':
                    log.warn(`WO ${event.workOrderId} stagnant for ${event.hoursStagnant}h.`);
                    break;
                case 'ai_neglect_retriggered':
                    log.info(`WO ${event.workOrderId} AI suggestion re-dispatched.`);
                    break;
                default:
                    break;
            }
        };

        // ── asset_events ──────────────────────────────────────────────────
        const onAssetEvent = (event) => {
            if (!isMountedRef.current) return;
            log.debug(`[WS:asset_events] type=${event.type}`);
            safeDispatch({ type: ACTIONS.RECORD_WS_EVENT, payload: { channel: WS_CHANNELS.ASSETS, ...event } });
            
            if (['rul_updated', 'health_score_changed', 'critical_threshold_crossed'].includes(event.type)) {
                cacheInvalidate(CACHE_KEYS.ASSET_HEALTH);
                fetchAssetHealth(null).catch(() => {});
            }
        };

        // Register all listeners
        const unsubs = [
            webSocketService.subscribe?.(WS_CHANNELS.WATCHDOG,    onWatchdogEvent)  ?? (() => {}),
            webSocketService.subscribe?.(WS_CHANNELS.WORK_ORDERS,  onWorkOrderEvent) ?? (() => {}),
            webSocketService.subscribe?.(WS_CHANNELS.ASSETS,       onAssetEvent)     ?? (() => {}),
        ].filter(Boolean);

        wsUnsubs.current = unsubs;
        log.info(`WebSocket listeners registered (${unsubs.length} channels).`);

    }, [safeDispatch, userRoleLevel, toast, fetchAssetHealth, fetchGlobalMetrics]);

    // ─────────────────────────────────────────────────────────────────────────
    // INITIALIZATION EFFECT
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        isMountedRef.current  = true;
        abortRef.current      = new AbortController();
        const { signal }      = abortRef.current;

        log.group('AIInsightsProvider — mount');
        log.info(`User: ${currentUser?.name ?? 'unknown'} | Role: ${userRole} (level ${userRoleLevel})`);

        // Initial data fetches (parallel)
        Promise.allSettled([
            fetchAdminConfig(signal),
            fetchWatchdogHealth(signal),
            fetchGlobalMetrics(signal),
            fetchAssetHealth(signal),
        ]).then(results => {
            results.forEach((r, i) => {
                if (r.status === 'rejected') {
                    log.error(`Init fetch ${i} failed:`, r.reason);
                }
            });
            log.info('Initial data fetch complete.');
        });

        // Attach WebSocket
        attachWebSocketListeners();

        log.groupEnd();

        // Cleanup on unmount
        return () => {
            isMountedRef.current = false;
            abortRef.current?.abort();

            // Detach WS listeners
            wsUnsubs.current.forEach(unsub => {
                try { unsub(); } catch { /* ignore */ }
            });
            wsUnsubs.current = [];

            // Clear polling intervals
            clearInterval(configIntervalRef.current);
            clearInterval(watchdogIntervalRef.current);

            log.info('AIInsightsProvider — unmount, all listeners removed.');
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Mount-only — all deps are stable refs or useCallback-memoized

    // ─────────────────────────────────────────────────────────────────────────
    // POLLING INTERVALS — wired to config values
    // Restarts whenever config changes so interval values are always fresh.
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        clearInterval(configIntervalRef.current);
        clearInterval(watchdogIntervalRef.current);

        configIntervalRef.current = setInterval(() => {
            if (!isMountedRef.current) return;
            fetchAdminConfig(null).catch(() => {});
        }, state.config.aiRefreshIntervalMs);

        watchdogIntervalRef.current = setInterval(() => {
            if (!isMountedRef.current) return;
            fetchWatchdogHealth(null).catch(() => {});
            fetchGlobalMetrics(null).catch(() => {});
        }, state.config.watchdogPollIntervalMs);

        return () => {
            clearInterval(configIntervalRef.current);
            clearInterval(watchdogIntervalRef.current);
        };
    }, [
        state.config.aiRefreshIntervalMs,
        state.config.watchdogPollIntervalMs,
        fetchAdminConfig,
        fetchWatchdogHealth,
        fetchGlobalMetrics
    ]);

    // ─────────────────────────────────────────────────────────────────────────
    // DERIVED METRICS (memoized — recompute only when source data changes)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Operational health roll-up — single object that summarises the system's
     * current health across SLA, assets, and work orders.
     */
    const operationalHealth = useMemo(() => {
        const efficiency = state.watchdogHealth?.last_scan?.efficiency_rating ?? globalMetrics.efficiency ?? 100;

        return {
            efficiency,
            openCount:          globalMetrics.totalOpen ?? 0,
            breachedCount:      globalMetrics.breached ?? 0,
            atRiskCount:        globalMetrics.atRisk ?? 0,
            criticalUnassigned: globalMetrics.unassignedCritical ?? 0,
            stagnantCount:      globalMetrics.stagnant ?? 0,
            aiNeglectCount:     globalMetrics.aiNeglect ?? 0,
            healthLabel: efficiency >= 90 ? 'excellent'
                       : efficiency >= 75 ? 'good'
                       : efficiency >= 55 ? 'fair'
                       : 'critical',
        };
    }, [globalMetrics, state.watchdogHealth]);

    /**
     * Asset health roll-up — surfaces ML-derived RUL and criticality distribution.
     */
    const assetHealthSummary = useMemo(() => {
        const snapshot = state.assetHealth;
        if (!snapshot) return null;

        return {
            totalAssets:      snapshot.asset_count ?? 0,
            criticalAssets:   snapshot.critical_count ?? 0,
            warningAssets:    snapshot.warning_count ?? 0,
            healthyAssets:    snapshot.healthy_count ?? 0,
            avgRulDays:       snapshot.avg_rul_days ?? null,
            predictedFailures: snapshot.predicted_failures_30d ?? 0,
        };
    }, [state.assetHealth]);

    /**
     * Live event feed — last 50 WebSocket events for the real-time activity log.
     */
    const liveEventFeed = useMemo(() => state.liveEvents, [state.liveEvents]);

    /**
     * Role-aware feature flags — gates premium insights to higher-level roles.
     */
    const featureFlags = useMemo(() => ({
        canViewWatchdogHealth:     userRoleLevel >= ROLE_LEVELS.manager,
        canViewPredictiveInsights: state.config.enablePredictiveInsights && userRoleLevel >= ROLE_LEVELS.supervisor,
        canViewDispatchSuggestions: state.config.enableDispatchSuggestions,
        canViewSlaForecasting:     state.config.enableSlaForecasting && userRoleLevel >= ROLE_LEVELS.supervisor,
        canManageConfig:           userRoleLevel >= ROLE_LEVELS.admin,
        userRoleLevel,
        userRole,
    }), [state.config, userRoleLevel, userRole]);

    // ─────────────────────────────────────────────────────────────────────────
    // MANUAL REFRESH ACTIONS (exposed for refresh buttons in the UI)
    // ─────────────────────────────────────────────────────────────────────────

    const refresh = useCallback(async () => {
        log.info('Manual refresh triggered.');
        cacheInvalidate(); // Flush entire namespace
        await Promise.allSettled([
            fetchAdminConfig(null),
            fetchWatchdogHealth(null),
            fetchGlobalMetrics(null),
            fetchAssetHealth(null),
        ]);
        insightsData?.refresh?.(); // Delegate to the underlying useAIInsights hook
    }, [fetchAdminConfig, fetchWatchdogHealth, fetchGlobalMetrics, fetchAssetHealth, insightsData]);

    const refreshWatchdog = useCallback(() => {
        cacheInvalidate(CACHE_KEYS.WATCHDOG);
        return fetchWatchdogHealth(null);
    }, [fetchWatchdogHealth]);

    const refreshAssets = useCallback(() => {
        cacheInvalidate(CACHE_KEYS.ASSET_HEALTH);
        return fetchAssetHealth(null);
    }, [fetchAssetHealth]);

    // ─────────────────────────────────────────────────────────────────────────
    // MEMOIZED CONTEXT VALUE
    // ─────────────────────────────────────────────────────────────────────────

    const contextValue = useMemo(() => ({

        // ── Core AI insights (from useAIInsights hook) ──────────────────────
        ...insightsData,

        // ── Operational health ───────────────────────────────────────────────
        operationalHealth,
        assetHealthSummary,

        // ── Watchdog ─────────────────────────────────────────────────────────
        watchdogHealth:    state.watchdogHealth,
        watchdogLoading:   state.isLoadingWatchdog,

        // ── Live feed ────────────────────────────────────────────────────────
        liveEventFeed,

        // ── Config ───────────────────────────────────────────────────────────
        config:            state.config,
        configLoading:     state.isLoadingConfig,

        // ── Feature flags ────────────────────────────────────────────────────
        featureFlags,

        // ── Error state ──────────────────────────────────────────────────────
        error:             state.error,
        clearError:        () => safeDispatch({ type: ACTIONS.RESET_ERROR }),

        // ── Loading states ───────────────────────────────────────────────────
        isLoadingAssets:   state.isLoadingAssets,

        // ── Actions ──────────────────────────────────────────────────────────
        refresh,
        refreshWatchdog,
        refreshAssets,

        // ── Exposed State Setters for Pages ──────────────────────────────────
        setLocalWorkOrders: setActiveWorkOrders,
        currentUser,

    }), [
        insightsData,
        operationalHealth,
        assetHealthSummary,
        state.watchdogHealth,
        state.isLoadingWatchdog,
        state.isLoadingConfig,
        state.isLoadingAssets,
        state.config,
        state.error,
        liveEventFeed,
        featureFlags,
        refresh,
        refreshWatchdog,
        refreshAssets,
        currentUser,
        safeDispatch,
    ]);

    return (
        <AIInsightsContext.Provider value={contextValue}>
            {children}
        </AIInsightsContext.Provider>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSUMER HOOKS
// Granular so components only subscribe to the slice they need.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Full context access — use sparingly; prefer the granular hooks below.
 */
export const useGlobalAIInsights = () => {
    const context = useContext(AIInsightsContext);
    if (!context) {
        throw new Error(`useGlobalAIInsights must be used within an AIInsightsProvider`);
    }
    return context;
};

/**
 * AI-derived insights + loading/error state only.
 * Safe for any component that renders AI suggestion cards.
 */
export const useAIInsightsData = () => {
    const { insights, isLoading, isRefreshing, error, refresh, lastUpdated, setLocalWorkOrders } =
        useGlobalAIInsights();
    return { insights, isLoading, isRefreshing, error, refresh, lastUpdated, setLocalWorkOrders };
};

/**
 * Operational health roll-up only.
 * Ideal for KPI headers, summary cards, and dashboard widgets.
 */
export const useOperationalHealth = () => {
    const { operationalHealth, watchdogHealth, watchdogLoading, refreshWatchdog } =
        useGlobalAIInsights();
    return { operationalHealth, watchdogHealth, watchdogLoading, refreshWatchdog };
};

/**
 * Asset health and RUL predictions only.
 * Ideal for the PredictiveHealthDashboard and LifecycleCostWidget.
 */
export const useAssetHealthInsights = () => {
    const { assetHealthSummary, isLoadingAssets, refreshAssets, featureFlags } =
        useGlobalAIInsights();
    return { assetHealthSummary, isLoadingAssets, refreshAssets, featureFlags };
};

/**
 * Live WebSocket event feed only.
 * Ideal for the real-time activity ticker or the diagnostics panel.
 */
export const useLiveEventFeed = () => {
    const { liveEventFeed } = useGlobalAIInsights();
    return liveEventFeed;
};

/**
 * Feature flags + role level only.
 * Use in any component that conditionally renders based on the user's role.
 */
export const useAIFeatureFlags = () => {
    const { featureFlags } = useGlobalAIInsights();
    return featureFlags;
};

/**
 * Admin config thresholds only.
 * Use in the Settings modal or any component that reads watchdog thresholds.
 */
export const useWatchdogConfig = () => {
    const { config, configLoading } = useGlobalAIInsights();
    return { config, configLoading };
};

/**
 * SLA-specific slices — breached and at-risk lists with the health summary.
 * Ideal for the SlaComplianceDashboard and the WorkOrderPage SLA panel.
 */
export const useSlaInsights = () => {
    const { operationalHealth } = useGlobalAIInsights();
    return { operationalHealth };
};

/**
 * Manual refresh actions only.
 * Pair with a refresh button in any dashboard header.
 */
export const useInsightsRefresh = () => {
    const { refresh, refreshWatchdog, refreshAssets } = useGlobalAIInsights();
    return { refresh, refreshWatchdog, refreshAssets };
};

// ─────────────────────────────────────────────────────────────────────────────
// INTERNAL HELPER: CONFIG NORMALIZER
// Maps the raw admin_configurations rows to the shape the context expects.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Array<{config_key: string, config_value: string|object}>} rows
 * @returns {object} Normalized config matching DEFAULT_CONFIG shape
 */
const normalizeAdminConfig = (rows) => {
    const cfg = { ...DEFAULT_CONFIG };

    const parse = (raw) => {
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return parsed?.value !== undefined ? parsed.value : parsed;
        } catch {
            return raw;
        }
    };

    for (const row of rows) {
        const val = parse(row.config_value);
        switch (row.config_key) {
            case 'ai_refresh_interval_ms':          cfg.aiRefreshIntervalMs       = Number(val) || cfg.aiRefreshIntervalMs;    break;
            case 'watchdog_poll_interval_ms':        cfg.watchdogPollIntervalMs    = Number(val) || cfg.watchdogPollIntervalMs;  break;
            case 'watchdog_sla_at_risk_hours':       cfg.slaAtRiskHours            = Number(val) || cfg.slaAtRiskHours;          break;
            case 'watchdog_stagnant_hours':          cfg.stagnantHours             = Number(val) || cfg.stagnantHours;           break;
            case 'watchdog_ai_neglect_hours':        cfg.aiNeglectHours            = Number(val) || cfg.aiNeglectHours;          break;
            case 'watchdog_unassigned_critical_hours': cfg.unassignedCriticalHours = Number(val) || cfg.unassignedCriticalHours; break;
            case 'enable_predictive_insights':       cfg.enablePredictiveInsights  = Boolean(val);                               break;
            case 'enable_dispatch_suggestions':      cfg.enableDispatchSuggestions = Boolean(val);                               break;
            case 'enable_sla_forecasting':           cfg.enableSlaForecasting      = Boolean(val);                               break;
            case 'max_insight_age_days':             cfg.maxInsightAge             = Number(val) || cfg.maxInsightAge;           break;
            default: break; 
        }
    }

    return cfg;
};

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT EXPORT
// ─────────────────────────────────────────────────────────────────────────────

export default AIInsightsContext;